import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GlobalLoadingSpinner from "../../components/GlobalLoadingSpinner";
import { fetchAdminLogPayload, fetchAdminLogs } from "./adminLogsApi";
import {
  calendarDayToIso,
  formatAdminLogDateTime,
  logsTimeZoneLabel,
} from "./adminLogsDateTime";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const CALL_SITE_LOG_TYPES = [
  { value: "api_http", label: "API HTTP" },
  { value: "redemption_change", label: "Redemption change" },
  { value: "n8n_redemption_webhook", label: "n8n redemption webhook" },
  { value: "force_xero_bills", label: "Force Xero bills" },
];

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];

function logTypeLabel(value) {
  const match = CALL_SITE_LOG_TYPES.find((t) => t.value === value);
  if (match) return match.label;
  if (value === "admin_log_payload_view") return "Admin payload view";
  return value || "—";
}

function statusChip(row) {
  const code = row.statusCode;
  if (code >= 500) return <Chip size="small" label={code} color="error" />;
  if (code >= 400) return <Chip size="small" label={code} color="warning" />;
  if (code) return <Chip size="small" label={code} color="success" variant="outlined" />;
  if (row.errorMessage) return <Chip size="small" label="Error" color="error" />;
  return "—";
}

const AdminLogs = () => {
  const [region, setRegion] = useState("");
  const [logType, setLogType] = useState("");
  const [method, setMethod] = useState("");
  const [statusCode, setStatusCode] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [route, setRoute] = useState("");
  const [urlSearch, setUrlSearch] = useState("");
  const [actorEmail, setActorEmail] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [merchantStoreId, setMerchantStoreId] = useState("");
  const [rego, setRego] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [payloadLoading, setPayloadLoading] = useState(false);
  const [payloadError, setPayloadError] = useState("");
  const [payloadData, setPayloadData] = useState(null);
  const [payloadSource, setPayloadSource] = useState("");

  const queryFilters = useMemo(() => {
    const from = calendarDayToIso(fromDate, region || "AU", false);
    const to = calendarDayToIso(toDate, region || "AU", true);
    return {
      page: page + 1,
      limit: rowsPerPage,
      region: region || undefined,
      logType: logType || undefined,
      method: method || undefined,
      statusCode: statusCode || undefined,
      errorsOnly,
      route: route.trim() || undefined,
      q: urlSearch.trim() || undefined,
      actorEmail: actorEmail.trim() || undefined,
      customerId: customerId.trim() || undefined,
      merchantStoreId: merchantStoreId.trim() || undefined,
      rego: rego.trim() || undefined,
      from: from || undefined,
      to: to || undefined,
    };
  }, [
    page,
    rowsPerPage,
    region,
    logType,
    method,
    statusCode,
    errorsOnly,
    route,
    urlSearch,
    actorEmail,
    customerId,
    merchantStoreId,
    rego,
    fromDate,
    toDate,
  ]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchAdminLogs(queryFilters);
      if (result?.success) {
        setRows(result.data?.rows || []);
        setTotal(result.data?.total ?? 0);
      } else {
        setError(result?.message || "Failed to load logs.");
        setRows([]);
        setTotal(0);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Unable to load logs.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [queryFilters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const openPayload = async (row) => {
    setSelectedRow(row);
    setDrawerOpen(true);
    setPayloadLoading(true);
    setPayloadError("");
    setPayloadData(null);
    setPayloadSource("");
    try {
      const result = await fetchAdminLogPayload(row._id);
      if (result?.success) {
        setPayloadData(result.data?.payload);
        setPayloadSource(result.data?.source || "");
      } else {
        setPayloadError(result?.message || "Failed to load payload.");
      }
    } catch (err) {
      setPayloadError(err.response?.data?.message || "Unable to load payload.");
    } finally {
      setPayloadLoading(false);
    }
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
    setPayloadData(null);
    setPayloadError("");
  };

  const resetFilters = () => {
    setRegion("");
    setLogType("");
    setMethod("");
    setStatusCode("");
    setErrorsOnly(false);
    setRoute("");
    setUrlSearch("");
    setActorEmail("");
    setCustomerId("");
    setMerchantStoreId("");
    setRego("");
    setFromDate("");
    setToDate("");
    setPage(0);
  };

  const tzLabel = logsTimeZoneLabel(region);

  return (
    <div className="page admin-logs flex flex-column items-center p-4 w-full">
      <div className="pageTitle flex flex-col w-full text-center lg:flex-row lg:text-left lg:justify-between mb-4 gap-3">
        <div>
          <h2>Application logs</h2>
          <Typography variant="body2" color="text.secondary">
            API and audit metadata with encrypted payloads in S3. Times shown in {tzLabel}.
          </Typography>
        </div>
        <Button variant="outlined" onClick={resetFilters}>
          Reset filters
        </Button>
      </div>

      <Paper sx={{ p: 2, mb: 2, width: "100%" }} elevation={1}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          Filters
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
            gap: 2,
          }}
        >
          <TextField
            label={`From (${tzLabel})`}
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(0);
            }}
          />
          <TextField
            label={`To (${tzLabel})`}
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(0);
            }}
          />
          <FormControl size="small">
            <InputLabel id="logs-region">Region</InputLabel>
            <Select
              labelId="logs-region"
              label="Region"
              value={region}
              onChange={(e) => {
                setRegion(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="AU">AU</MenuItem>
              <MenuItem value="NZ">NZ</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel id="logs-type">Log type</InputLabel>
            <Select
              labelId="logs-type"
              label="Log type"
              value={logType}
              onChange={(e) => {
                setLogType(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All call sites</MenuItem>
              {CALL_SITE_LOG_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
              <MenuItem value="admin_log_payload_view">Admin payload view</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Route contains"
            size="small"
            value={route}
            onChange={(e) => {
              setRoute(e.target.value);
              setPage(0);
            }}
          />
          <FormControl size="small">
            <InputLabel id="logs-method">Method</InputLabel>
            <Select
              labelId="logs-method"
              label="Method"
              value={method}
              onChange={(e) => {
                setMethod(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">Any</MenuItem>
              {HTTP_METHODS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Status code"
            size="small"
            type="number"
            value={statusCode}
            onChange={(e) => {
              setStatusCode(e.target.value);
              setPage(0);
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={errorsOnly}
                onChange={(e) => {
                  setErrorsOnly(e.target.checked);
                  setPage(0);
                }}
              />
            }
            label="Errors only (4xx/5xx or error message)"
          />
          <TextField
            label="Request URL search"
            size="small"
            value={urlSearch}
            onChange={(e) => {
              setUrlSearch(e.target.value);
              setPage(0);
            }}
          />
          <TextField
            label="User email"
            size="small"
            value={actorEmail}
            onChange={(e) => {
              setActorEmail(e.target.value);
              setPage(0);
            }}
          />
          <TextField
            label="Customer ID"
            size="small"
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setPage(0);
            }}
          />
          <TextField
            label="Merchant store ID"
            size="small"
            value={merchantStoreId}
            onChange={(e) => {
              setMerchantStoreId(e.target.value);
              setPage(0);
            }}
          />
          <TextField
            label="Vehicle rego"
            size="small"
            value={rego}
            onChange={(e) => {
              setRego(e.target.value);
              setPage(0);
            }}
          />
        </Box>
      </Paper>

      {error && (
        <Typography color="error" sx={{ mb: 2, width: "100%" }}>
          {error}
        </Typography>
      )}

      <TableContainer component={Paper} sx={{ width: "100%" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <GlobalLoadingSpinner />
          </Box>
        )}
        {!loading && (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Rego</TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell align="right">Payload</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No log entries match these filters.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {formatAdminLogDateTime(row.createdAt, region || row.region)}
                    </TableCell>
                    <TableCell>{row.region || "—"}</TableCell>
                    <TableCell>{logTypeLabel(row.logType)}</TableCell>
                    <TableCell>{row.method || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }} title={row.route}>
                      {row.route || "—"}
                    </TableCell>
                    <TableCell>{statusChip(row)}</TableCell>
                    <TableCell sx={{ maxWidth: 160 }} title={row.actorEmail}>
                      {row.actorEmail || "—"}
                    </TableCell>
                    <TableCell>{row.vehicleRego || "—"}</TableCell>
                    <TableCell sx={{ maxWidth: 240 }} title={row.responseSummary || row.errorMessage}>
                      {row.errorMessage || row.responseSummary || "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openPayload(row)}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
          </>
        )}
      </TableContainer>

      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer} PaperProps={{ sx: { width: { xs: "100%", sm: 480 } } }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Log payload</Typography>
          <IconButton onClick={closeDrawer} aria-label="Close payload drawer">
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ px: 2, pb: 2 }}>
          {selectedRow && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {logTypeLabel(selectedRow.logType)} · {selectedRow._id}
              {payloadSource ? ` · ${payloadSource}` : ""}
            </Typography>
          )}
          {payloadLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {payloadError && <Typography color="error">{payloadError}</Typography>}
          {!payloadLoading && payloadData != null && (
            <Box
              component="pre"
              sx={{
                bgcolor: "grey.100",
                p: 2,
                borderRadius: 1,
                overflow: "auto",
                fontSize: 12,
                maxHeight: "calc(100vh - 160px)",
              }}
            >
              {JSON.stringify(payloadData, null, 2)}
            </Box>
          )}
        </Box>
      </Drawer>
    </div>
  );
};

export default AdminLogs;
